<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  unit: { type: Object, default: null },
  parentId: { type: [Number, String], default: null },
  units: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();

const UNIT_TYPES = ['department', 'division', 'team', 'unit'];

const form = ref({
  name: '',
  unit_type: 'team',
  parent_id: null,
  team_id: null,
  description: '',
});

const isEdit = computed(() => Boolean(props.unit?.id));

// A unit can never be moved inside itself or inside one of its own children.
const parentOptions = computed(() => {
  if (!isEdit.value) return props.units;
  const forbiddenPrefix = props.unit.path;
  return props.units.filter(
    unit => !String(unit.path || '').startsWith(forbiddenPrefix)
  );
});

watch(
  () => [props.show, props.unit, props.parentId],
  () => {
    if (!props.show) return;
    form.value = props.unit
      ? {
          name: props.unit.name,
          unit_type: props.unit.unit_type,
          parent_id: props.unit.parent_id,
          team_id: props.unit.team_id,
          description: props.unit.description,
        }
      : {
          name: '',
          unit_type: props.parentId ? 'team' : 'department',
          parent_id: props.parentId,
          team_id: null,
          description: '',
        };
  },
  { immediate: true }
);

const onSubmit = () => {
  if (!form.value.name.trim()) return;
  emit('submit', { ...form.value, id: props.unit?.id });
};
</script>

<template>
  <Modal :show="show" @close="emit('close')">
    <form class="flex flex-col gap-4 p-8" @submit.prevent="onSubmit">
      <h3 class="text-base font-medium text-n-slate-12">
        {{
          isEdit
            ? t('ORG_ACCESS.UNIT_FORM.EDIT_TITLE')
            : t('ORG_ACCESS.UNIT_FORM.CREATE_TITLE')
        }}
      </h3>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ORG_ACCESS.UNIT_FORM.NAME') }}
        <input
          v-model="form.name"
          type="text"
          :placeholder="t('ORG_ACCESS.UNIT_FORM.NAME_PLACEHOLDER')"
        />
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ORG_ACCESS.UNIT_FORM.TYPE') }}
        <select v-model="form.unit_type">
          <option v-for="type in UNIT_TYPES" :key="type" :value="type">
            {{ t(`ORG_ACCESS.UNIT_TYPE.${type.toUpperCase()}`) }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ORG_ACCESS.UNIT_FORM.PARENT') }}
        <select v-model="form.parent_id">
          <option :value="null">
            {{ t('ORG_ACCESS.UNIT_FORM.PARENT_NONE') }}
          </option>
          <option
            v-for="option in parentOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ '— '.repeat(option.depth) }}{{ option.name }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ORG_ACCESS.UNIT_FORM.TEAM') }}
        <select v-model="form.team_id">
          <option :value="null">
            {{ t('ORG_ACCESS.UNIT_FORM.TEAM_NONE') }}
          </option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <span class="text-xs text-n-slate-11">
          {{ t('ORG_ACCESS.UNIT_FORM.TEAM_HINT') }}
        </span>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-12">
        {{ t('ORG_ACCESS.UNIT_FORM.DESCRIPTION') }}
        <textarea v-model="form.description" rows="2" />
      </label>

      <div class="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          :label="t('ORG_ACCESS.UNIT_FORM.CANCEL')"
          @click="emit('close')"
        />
        <Button type="submit" :label="t('ORG_ACCESS.UNIT_FORM.SAVE')" />
      </div>
    </form>
  </Modal>
</template>
